<script lang="ts">
 /**
 * Camera viewfinder that streams the rear-facing camera.
 *
 * @remarks
 * Requests the rear camera on mount and keeps its live stream available
 * for barcode scanning. In the Android shell, CameraX renders beneath this
 * interface and returns full-quality still photos through the file chooser.
 * Pauses/resumes the video feed when the `frozen` prop changes
 * (frozen during processing and suggestion states to visually signal
 * that analysis is happening).
 *
 * Falls back to a gradient with the error message when the camera
 * is unavailable (desktop browser, denied permissions, etc.).
 *
 * Provides a default slot for overlays (e.g. {@link CaptureScanOverlay}).
 * Overlays are absolutely positioned within this component's bounds.
 */
</script>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, shallowRef, useTemplateRef, watch } from "vue"

import { useNativeCamera } from "@/composables/useNativeCamera"
import type { NativeCameraFlashMode } from "@/composables/useNativeCamera"
import { errorMessage } from "@/lib/errors"
import { ICON } from "@/lib/icons"

const props = withDefaults(
  defineProps<{
    /** When true, pauses the video feed (dims with opacity). Used during processing/suggestion. */
    frozen: boolean
    /** Let the native Android camera return several photos in one session. */
    multiple?: boolean
    /** Whether the existing browser QR scanner owns the visible viewfinder. */
    scannerActive?: boolean
    /** Raise zoom chips above the normal capture screen's floating action stack. */
    zoomPosition?: "default" | "above-capture-controls"
  }>(),
  { multiple: false, scannerActive: false, zoomPosition: "default" },
)

const emit = defineEmits<{
  "photos-selected": [files: File[]]
  "native-preview": [active: boolean]
  "update:busy": [busy: boolean]
  "scanner-ready": [video: HTMLVideoElement]
}>()

const viewfinderEl = useTemplateRef<HTMLDivElement>("viewfinder")
const nativeCamera = useNativeCamera()

watch(nativeCamera.status, (status) => emit("update:busy", status === "saving"))

/** Template ref for the `<video>` element that displays the camera stream. */
const videoEl = shallowRef<HTMLVideoElement | null>(null)

/** The active MediaStream from getUserMedia. Null before camera starts or after stop. */
const stream = shallowRef<MediaStream | null>(null)

/** Human-readable camera error message, or null if camera is working. */
const cameraError = shallowRef<string | null>(null)
const focusPoint = shallowRef<{ x: number; y: number } | null>(null)
const pendingPhotos = shallowRef<File[]>([])
const reviewUrl = shallowRef<string | null>(null)
const flashMode = shallowRef<NativeCameraFlashMode>("auto")
const selectedZoomRatio = shallowRef(1)
const pointers = new Map<number, { x: number; y: number }>()
let pinchStarted = false
let pinchDistance = 0
let pinchZoom = 0
let currentZoom = 0
let focusTimer: ReturnType<typeof setTimeout> | null = null
let scannerStartPending = false
let restartPending = false

const flashModes: NativeCameraFlashMode[] = ["off", "auto", "on"]
const zoomPresets = [0.5, 1, 2, 5]
const supportedZoomPresets = computed(() => {
  const state = nativeCamera.zoomState.value
  if (!state) return []
  return zoomPresets.flatMap((labelRatio) => {
    const cameraRatio =
      labelRatio === 0.5 &&
      labelRatio < state.minimumRatio &&
      Math.round(state.minimumRatio * 10) / 10 === labelRatio
        ? state.minimumRatio
        : labelRatio
    if (cameraRatio < state.minimumRatio || cameraRatio > state.maximumRatio) return []
    return [{ labelRatio, cameraRatio }]
  })
})
const zoomPositionClass = computed(() =>
  props.zoomPosition === "above-capture-controls" ? "bottom-72" : "bottom-32",
)
const flashLabel = computed(() => {
  switch (flashMode.value) {
    case "off":
      return "Off"
    case "on":
      return "On"
    default:
      return "Auto"
  }
})
const flashIcon = computed(() => {
  switch (flashMode.value) {
    case "off":
      return ICON.flashOff
    case "on":
      return ICON.flashOn
    default:
      return ICON.flashAuto
  }
})

const nativeError = computed(() => {
  switch (nativeCamera.status.value) {
    case "permission-required":
      return { message: "Camera access is required", action: "permission" as const }
    case "permission-denied":
      return { message: "Camera access is turned off for Ninjanizr", action: "settings" as const }
    case "error":
      return { message: "The camera could not start", action: "retry" as const }
    default:
      return null
  }
})

watch(nativeCamera.zoomState, (state) => {
  currentZoom = state!.linearZoom
  selectedZoomRatio.value = state!.ratio
})

function cycleFlash(): void {
  const currentIndex = flashModes.indexOf(flashMode.value)
  flashMode.value = flashModes[(currentIndex + 1) % flashModes.length]!
  nativeCamera.setFlash(flashMode.value)
}

function selectZoomRatio(cameraRatio: number): void {
  selectedZoomRatio.value = cameraRatio
  nativeCamera.setZoomRatio(cameraRatio)
}

function isSelectedZoomRatio(ratio: number): boolean {
  return Math.abs(selectedZoomRatio.value - ratio) < 0.01
}

function zoomRatioLabel(ratio: number): string {
  if (ratio === 0.5) return ".5"
  if (ratio === 1) return "1x"
  return String(ratio)
}

function clearReview(): void {
  if (reviewUrl.value) URL.revokeObjectURL(reviewUrl.value)
  reviewUrl.value = null
  pendingPhotos.value = []
}

function reviewPhotos(files: File[]): void {
  clearReview()
  pendingPhotos.value = files
  reviewUrl.value = URL.createObjectURL(files[0]!)
}

function keepPhotos(): void {
  const files = pendingPhotos.value
  clearReview()
  emit("photos-selected", files)
}

function retakePhoto(): void {
  clearReview()
}

function retryNativeCamera(): void {
  restartPending = true
  nativeCamera.stopPreview()
}

function pointerDistance(): number {
  const [first, second] = [...pointers.values()] as [
    { x: number; y: number },
    { x: number; y: number },
  ]
  return Math.hypot(second.x - first.x, second.y - first.y)
}

function onPointerDown(event: PointerEvent): void {
  if (!nativeCamera.available.value || event.target !== event.currentTarget) return
  pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
  if (pointers.size === 1) {
    pinchStarted = false
  } else if (pointers.size === 2) {
    pinchStarted = true
    pinchDistance = pointerDistance()
    pinchZoom = currentZoom
  }
}

function onPointerMove(event: PointerEvent): void {
  if (!pointers.has(event.pointerId)) return
  pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
  if (pointers.size !== 2 || pinchDistance === 0) return

  const nextZoom = Math.min(1, Math.max(0, pinchZoom + (pointerDistance() - pinchDistance) / 300))
  if (Math.abs(nextZoom - currentZoom) < 0.01) return
  currentZoom = nextZoom
  nativeCamera.setZoom(Number(nextZoom.toFixed(3)))
}

function onPointerUp(event: PointerEvent): void {
  if (!pointers.has(event.pointerId)) return
  pointers.delete(event.pointerId)

  if (pinchStarted) {
    if (pointers.size === 0) pinchStarted = false
    return
  }

  const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect()
  const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width))
  const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height))
  focusPoint.value = { x, y }
  nativeCamera.focus(x, y)
  if (focusTimer) clearTimeout(focusTimer)
  focusTimer = setTimeout(() => (focusPoint.value = null), 1500)
}

function onPointerCancel(event: PointerEvent): void {
  pointers.delete(event.pointerId)
  if (pointers.size === 0) pinchStarted = false
}

/**
 * Request camera access and bind the stream to the video element.
 *
 * @remarks
 * Uses the rear camera and requests a high-resolution preview. The preview
 * remains live for barcode scanning; still photos use the native camera.
 * On desktop, this typically falls back to the default webcam. If camera
 * access is denied or unavailable, sets `cameraError` for the fallback UI.
 */
async function startCamera() {
  try {
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    })
    // Guard: component may have unmounted while awaiting getUserMedia
    if (videoEl.value) {
      stream.value = mediaStream
      videoEl.value.srcObject = mediaStream
      if (props.scannerActive) emit("scanner-ready", videoEl.value)
    } else {
      mediaStream.getTracks().forEach((track) => track.stop())
    }
  } catch (err) {
    cameraError.value = errorMessage(err, "Camera not available")
  }
}

async function startBrowserScanner(): Promise<void> {
  await nextTick()
  await startCamera()
}

/**
 * Stop all camera tracks and release the media stream.
 *
 * @remarks
 * Called on component unmount. Stops each track individually to ensure
 * the camera LED turns off and the stream is fully released.
 */
function stopCamera() {
  stream.value?.getTracks().forEach((track) => track.stop())
  stream.value = null
}

/**
 * Watch the `frozen` prop to pause/resume the video feed.
 *
 * @remarks
 * When frozen, the video pauses on the last frame and dims (via CSS opacity).
 * This provides visual feedback that the app is processing without stopping
 * the camera entirely (so it can resume instantly).
 */
watch(
  () => props.frozen,
  (frozen) => {
    if (nativeCamera.available.value) return
    if (frozen) {
      videoEl.value?.pause()
    } else {
      videoEl.value?.play().catch(() => {
        // Browser blocked autoplay — user may need to tap to resume
      })
    }
  },
)

watch(
  () => props.scannerActive,
  (active) => {
    if (!nativeCamera.available.value) {
      if (active && videoEl.value) emit("scanner-ready", videoEl.value)
      return
    }

    if (active) {
      scannerStartPending = true
      nativeCamera.stopPreview()
    } else {
      scannerStartPending = false
      stopCamera()
      nativeCamera.startPreview(viewfinderEl.value!)
    }
  },
)

watch(nativeCamera.status, (status) => {
  if (restartPending && status === "inactive") {
    restartPending = false
    nativeCamera.startPreview(viewfinderEl.value!)
    return
  }
  if (!props.scannerActive || !scannerStartPending || status !== "inactive") return
  scannerStartPending = false
  void startBrowserScanner()
})

/**
 * Request a full-quality rear-camera photo and emit its original returned file.
 *
 * The Android shell intercepts this chooser and captures through the embedded
 * CameraX viewfinder. Browsers retain their standard media-capture fallback.
 * Calling `click()` synchronously preserves the originating user gesture.
 */
function openNativeCamera(): void {
  if (props.frozen || pendingPhotos.value.length > 0 || nativeCamera.status.value === "saving")
    return

  const input = document.createElement("input")
  input.type = "file"
  input.accept = "image/*"
  input.multiple = props.multiple
  input.setAttribute("capture", "environment")

  // WebView does not retain a detached file input while CameraX is open.
  // Keep it in the document until Android returns or cancels the chooser.
  input.hidden = true
  document.body.appendChild(input)
  const removeInput = () => input.remove()

  input.addEventListener(
    "change",
    () => {
      const files = Array.from(input.files ?? [])
      if (files.length > 0) reviewPhotos(files)
      removeInput()
    },
    { once: true },
  )
  input.addEventListener("cancel", removeInput, { once: true })
  input.click()
}

defineExpose({ openNativeCamera, stopScanner: stopCamera, videoEl })

onMounted(() => {
  if (nativeCamera.available.value && viewfinderEl.value) {
    emit("native-preview", true)
    nativeCamera.startPreview(viewfinderEl.value)
    if (props.scannerActive) {
      scannerStartPending = true
      nativeCamera.stopPreview()
    }
  } else {
    startCamera()
  }
})
onUnmounted(() => {
  if (focusTimer) clearTimeout(focusTimer)
  clearReview()
  nativeCamera.dispose()
  stopCamera()
})
</script>

<template>
  <div
    ref="viewfinder"
    class="relative h-full touch-none"
    :class="nativeCamera.available.value ? 'bg-transparent' : 'bg-inverted'"
    :data-native-preview="nativeCamera.available.value"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
  >
    <!-- Live camera feed -->
    <video
      v-if="!nativeCamera.available.value || scannerActive"
      ref="videoEl"
      autoplay
      playsinline
      muted
      class="absolute inset-0 h-full w-full object-cover"
      :class="frozen ? 'opacity-50' : ''"
      @click="openNativeCamera"
    />

    <!-- Fallback when camera not available -->
    <div
      v-if="cameraError"
      class="absolute inset-0 flex items-center justify-center"
      :style="{
        background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 50%, #475569 100%)',
      }"
    >
      <span class="text-xs text-white/30">{{ cameraError }}</span>
    </div>

    <div
      v-if="focusPoint"
      data-testid="focus-ring"
      class="pointer-events-none absolute size-12 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
      :style="{ left: `${focusPoint.x * 100}%`, top: `${focusPoint.y * 100}%` }"
    />

    <div
      v-if="nativeError"
      class="absolute inset-0 z-20 flex items-center justify-center bg-black/70 p-6"
    >
      <div class="flex max-w-sm flex-col gap-3 text-center text-white">
        <p>{{ nativeError.message }}</p>
        <UButton
          v-if="nativeError.action === 'permission'"
          data-testid="allow-camera"
          @click="nativeCamera.requestPermission"
        >
          Allow camera
        </UButton>
        <UButton
          v-else-if="nativeError.action === 'settings'"
          data-testid="open-camera-settings"
          @click="nativeCamera.openSettings"
        >
          Open settings
        </UButton>
        <UButton v-else data-testid="retry-camera" @click="retryNativeCamera">Retry</UButton>
      </div>
    </div>

    <button
      v-if="nativeCamera.available.value && !scannerActive && !frozen && !reviewUrl"
      type="button"
      data-testid="flash-mode"
      :aria-label="`Flash: ${flashLabel}`"
      class="absolute top-4 right-4 z-10 flex min-h-12 items-center gap-1 rounded-full bg-black/70 px-4 text-sm font-medium text-white backdrop-blur-sm focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
      @click="cycleFlash"
    >
      <UIcon :name="flashIcon" class="size-4" />
      {{ flashLabel }}
    </button>

    <div
      v-if="
        nativeCamera.available.value &&
        !scannerActive &&
        !frozen &&
        !reviewUrl &&
        supportedZoomPresets.length
      "
      role="group"
      aria-label="Camera zoom"
      class="absolute inset-x-0 z-10 flex justify-center gap-2"
      :class="zoomPositionClass"
    >
      <button
        v-for="preset in supportedZoomPresets"
        :key="preset.labelRatio"
        type="button"
        data-testid="zoom-ratio"
        :aria-label="`${preset.labelRatio} times zoom`"
        :aria-pressed="isSelectedZoomRatio(preset.cameraRatio)"
        class="flex size-12 items-center justify-center rounded-full border border-white/40 text-sm font-semibold backdrop-blur-sm focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
        :class="
          isSelectedZoomRatio(preset.cameraRatio) ? 'bg-white text-black' : 'bg-black/70 text-white'
        "
        @click="selectZoomRatio(preset.cameraRatio)"
      >
        {{ zoomRatioLabel(preset.labelRatio) }}
      </button>
    </div>

    <div
      v-if="reviewUrl"
      data-testid="capture-review"
      role="dialog"
      aria-modal="true"
      aria-label="Review captured photo"
      class="fixed inset-0 z-50 flex flex-col bg-black"
    >
      <img
        data-testid="capture-review-image"
        :src="reviewUrl"
        alt="Captured photo to review"
        class="min-h-0 w-full flex-1 object-contain"
      />
      <div
        class="grid grid-cols-2 gap-4 bg-black/90 p-4"
        :style="{ paddingBottom: 'max(1rem, var(--safe-bottom))' }"
      >
        <UButton
          data-testid="retake-photo"
          block
          size="xl"
          color="neutral"
          variant="outline"
          @click="retakePhoto"
        >
          Retake
        </UButton>
        <UButton data-testid="keep-photo" block size="xl" @click="keepPhotos">Keep</UButton>
      </div>
    </div>

    <!-- Overlays rendered via slot -->
    <slot />
  </div>
</template>
